import { useNavigate, useLocation } from 'react-router-dom';
import Loading from '../components/Loading';
import { useGetCompanyReportQuery } from '../services/casino/casinoApi';

const CasinoCompanyReportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get params from location state or URL
  let { fromDate, toDate, game, userId, gameName } = location.state || {};
  
  // Get userId from localStorage if not provided
  if (!userId) {
    const storedUser = localStorage.getItem('user_data');
    if (storedUser) {
      try {
        const userObj = JSON.parse(storedUser);
        userId = userObj.userId || '';
      } catch (e) {
        console.error('Error parsing user from localStorage:', e);
      }
    }
  }
  
  
  // Fetch company report using RTK Query
  const { data: reportData, isLoading, error } = useGetCompanyReportQuery({
    game,
    userId,
    fromDate,
    toDate
  }, {
    skip: !game || !userId,
  });
  
  const reportRows = reportData?.data || [];
  const viewUser = reportData?.viewUser || {};
  const totals = reportData?.summary || reportData?.totals || {};
  const canViewDownline = viewUser.userType !== 'Client';

  const numColor = (val) => {
    const n = Number(val ?? 0);
    return n < 0 ? 'text-red-600' : 'text-green-600';
  };

  const fmt = (val) => Number(val ?? 0).toFixed(2);
  
  const handleUsernameClick = (clickedUserId) => {
    // Navigate to same page with new userId
    navigate('/casino/company-report', {
      state: {
        game,
        userId: clickedUserId,
        gameName,
        fromDate,
        toDate
      }
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-[#3e525f] px-5 py-3 flex items-center justify-between">
              <span className="text-xl text-white font-normal uppercase">
                {gameName || game || 'CASINO REPORT'}
              </span>
              <button 
                onClick={handleBack}
                className="bg-white text-gray-700 px-4 py-1.5 hover:bg-gray-100 transition-colors"
              >
                Back
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center">{error?.message || 'Error loading data'}</div>
              ) : (
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-[#3e525f] text-white">
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Username</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Casino Amt.</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Total</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Casino Comm</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Total Comm</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Total Amount</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">My Share</th>
                      <th className="py-3 px-4 text-left font-medium">Net Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {reportRows.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-gray-500 border-t border-gray-200">
                          No data available
                        </td>
                      </tr>
                    ) : (
                      reportRows.map((row, index) => {
                        const isTotal = row.username?.toLowerCase() === 'total';
                        return (
                          <tr key={index} className={`hover:bg-gray-50 border-t border-gray-200 ${isTotal ? 'font-semibold bg-gray-50' : ''}`}>
                            <td className="py-3 px-4 text-gray-800 border-r border-gray-200">
                              {!isTotal && row.userId && canViewDownline && row.userType !== 'Client' ? (
                                <button
                                  onClick={() => handleUsernameClick(row.userId)}
                                  className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                                >
                                  <div className="font-semibold">{row.userId || row.username || '-'}</div>
                                  {row.name && <div className="text-xs text-gray-500 font-normal">{row.name}</div>}
                                </button>
                              ) : (
                                <span>
                                  <div className="font-semibold">{row.userId || row.username || '-'}</div>
                                  {row.name && <div className="text-xs text-gray-500">{row.name}</div>}
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 border-r border-gray-200"><span className={numColor(row.casinoAmt)}>{fmt(row.casinoAmt)}</span></td>
                            <td className="py-3 px-4 border-r border-gray-200"><span className={numColor(row.total)}>{fmt(row.total)}</span></td>
                            <td className="py-3 px-4 border-r border-gray-200"><span className={numColor(row.casinoComm)}>{fmt(row.casinoComm)}</span></td>
                            <td className="py-3 px-4 border-r border-gray-200"><span className={numColor(row.totalComm)}>{fmt(row.totalComm)}</span></td>
                            <td className="py-3 px-4 border-r border-gray-200"><span className={numColor(row.totalAmount)}>{fmt(row.totalAmount)}</span></td>
                            <td className="py-3 px-4 border-r border-gray-200"><span className={numColor(row.myShare)}>{fmt(row.myShare)}</span></td>
                            <td className="py-3 px-4"><span className={numColor(row.netAmount)}>{fmt(row.netAmount)}</span></td>
                          </tr>
                        );
                      })
                    )}
                    
                    {/* Summary/Total Row */}
                    {reportRows.length > 0 && totals && (
                      <tr className="bg-yellow-50 border-t-2 border-yellow-600 font-bold">
                        <td className="py-3 px-4 border-r border-gray-200">Total</td>
                        <td className="py-3 px-4 border-r border-gray-200"><span className={numColor(totals.casinoAmt)}>{fmt(totals.casinoAmt)}</span></td>
                        <td className="py-3 px-4 border-r border-gray-200"><span className={numColor(totals.total)}>{fmt(totals.total)}</span></td>
                        <td className="py-3 px-4 border-r border-gray-200"><span className={numColor(totals.casinoComm)}>{fmt(totals.casinoComm)}</span></td>
                        <td className="py-3 px-4 border-r border-gray-200"><span className={numColor(totals.totalComm)}>{fmt(totals.totalComm)}</span></td>
                        <td className="py-3 px-4 border-r border-gray-200"><span className={numColor(totals.totalAmount)}>{fmt(totals.totalAmount)}</span></td>
                        <td className="py-3 px-4 border-r border-gray-200"><span className={numColor(totals.myShare)}>{fmt(totals.myShare)}</span></td>
                        <td className="py-3 px-4"><span className={numColor(totals.netAmount)}>{fmt(totals.netAmount)}</span></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CasinoCompanyReportPage;
